import { useCallback, useMemo, useRef } from 'react';
import {
    listenForOAuth2PopupCallback,
    OAUTH2_POPUP_CALLBACK_PATH,
    type IOAuth2PopupCallbackErrorPayload,
    type OAuth2PopupCallbackMessageData,
} from '../../components/auth/oauth2-popup-callback';
import type { UrlParams } from '../../model';

const AUTH_POPUP_CLOSED_POLL_MS = 400;

const newOAuth2State = (): string => {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
        return crypto.randomUUID();
    }
    return `st-${Date.now()}-${Math.random().toString(36).slice(2, 12)}`;
};

export interface UsePillar2AuthFlowOptions {
    fetchAuthorizationUrl: (params: UrlParams, signal?: AbortSignal) => Promise<string>;
    postPillar2AuthLogout: (signal?: AbortSignal) => Promise<void>;
    onAuthCode: (code: string, redirectUri: string | null) => void | Promise<void>;
    onOAuthError: (payload: IOAuth2PopupCallbackErrorPayload) => void;
}

/**
 * Pillar 2 user-delegated OAuth: open authorize URL in a popup and forward authcode (+ redirect_uri) to the caller.
 * Cleanup on panel close via {@link cleanup}. After a successful or failed OAuth message, watchers are cleared internally.
 */
export function usePillar2AuthFlow(options: UsePillar2AuthFlowOptions) {
    const optionsRef = useRef(options);
    optionsRef.current = options;

    const oauthStateRef = useRef<string | null>(null);
    const oauthUnsubscribeRef = useRef<(() => void) | null>(null);
    const authPopupPollRef = useRef<number | null>(null);
    const redirectUriRef = useRef<string | null>(null);

    const cleanup = useCallback(async (performLogout?: boolean): Promise<void> => {
        if (authPopupPollRef.current !== null) {
            window.clearInterval(authPopupPollRef.current);
            authPopupPollRef.current = null;
        }
        if (oauthUnsubscribeRef.current !== null) {
            oauthUnsubscribeRef.current();
            oauthUnsubscribeRef.current = null;
        }
        redirectUriRef.current = null;
        oauthStateRef.current = null;

        if (performLogout) {
            try {
                await optionsRef.current.postPillar2AuthLogout();
            } catch {
                // best-effort clear
            }
        }
    }, []);

    const start = useCallback(async (): Promise<'opened' | 'no_url' | 'no_popup'> => {
        cleanup(true);

        const redirectUri = new URL(OAUTH2_POPUP_CALLBACK_PATH, window.location.origin).href;
        redirectUriRef.current = redirectUri;
        const state = newOAuth2State();
        const flowState = state;
        oauthStateRef.current = state;

        const authorizationUrl = await optionsRef.current.fetchAuthorizationUrl({ redirectUri, state });
        if (!authorizationUrl) {
            oauthStateRef.current = null;
            return 'no_url';
        }

        const authPopup = window.open(authorizationUrl, '_blank', 'popup=yes,width=600,height=720');
        if (!authPopup) {
            oauthStateRef.current = null;
            return 'no_popup';
        }

        const unsubscribe = listenForOAuth2PopupCallback((data: OAuth2PopupCallbackMessageData) => {
            if (oauthStateRef.current !== flowState) {
                return;
            }
            const messageState = data.payload.state;
            if (messageState !== flowState) {
                return;
            }

            oauthStateRef.current = null;

            if (data.status === 'authcode') {
                void (async () => {
                    try {
                        await optionsRef.current.onAuthCode(data.payload.code, redirectUriRef.current);
                    } finally {
                        cleanup();
                    }
                })();
            } else {
                optionsRef.current.onOAuthError(data.payload);
                cleanup();
            }
        });
        oauthUnsubscribeRef.current = unsubscribe;

        const pollId = window.setInterval(() => {
            if (!authPopup.closed) {
                return;
            }
            window.clearInterval(pollId);
            if (authPopupPollRef.current === pollId) {
                authPopupPollRef.current = null;
            }
            if (oauthStateRef.current === flowState) {
                oauthStateRef.current = null;
                oauthUnsubscribeRef.current?.();
                oauthUnsubscribeRef.current = null;
            }
        }, AUTH_POPUP_CLOSED_POLL_MS);
        authPopupPollRef.current = pollId;

        return 'opened';
    }, [cleanup]);

    return useMemo(() => ({ start, cleanup }), [start, cleanup]);
}
